import React, {
  createContext,
  PropsWithChildren,
  useState,
  useCallback,
  useMemo,
  Dispatch,
  SetStateAction,
} from 'react'
import type { CustomerSortOptions } from '../../../../../../graphql/api/types'

type HeaderData = {
  sort: CustomerSortOptions
  search: string
}

type HeaderSetters = {
  setSort: (options: CustomerSortOptions) => void
  setSearch: (search: string) => void
}

type Header = {
  data: HeaderData
  setters: HeaderSetters
}

type ListFarmsErrorState = {
  value: Error | null
  refetch: () => Promise<unknown>
}

type ListFarmsContextType = {
  header: Header
  error: ListFarmsErrorState & {
    set: (errorState: ListFarmsErrorState) => void
  }
  setSelectedFarmId: Dispatch<SetStateAction<string>>
  selectedFarmId: string
}

const INITIAL_DATA: ListFarmsContextType = {
  header: {
    data: {
      search: '',
      sort: (localStorage.getItem('FARM_PREFERRED_SORT_OPTION') as CustomerSortOptions) || 'MOST_RECENT',
    },
    setters: {
      setSort: () => undefined,
      setSearch: () => undefined,
    },
  },
  error: {
    value: null,
    refetch: async () => undefined,
    set: () => undefined,
  },
  setSelectedFarmId: () => undefined,
  selectedFarmId: '',
}

export const ListFarmsContext = createContext<ListFarmsContextType>(INITIAL_DATA)

export const ListFarmsProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const [selectedFarmId, setSelectedFarmId] = useState('')
  const [headerData, setHeaderData] = useState<HeaderData>(INITIAL_DATA.header.data)
  const [errorState, setErrorState] = useState<ListFarmsErrorState>({
    value: null,
    refetch: async () => undefined,
  })

  const setSort = useCallback((option: CustomerSortOptions): void => {
    setHeaderData((prev) => ({ ...prev, sort: option }))
  }, [])

  const setSearch = useCallback(
    (search: string): void => {
      if (search !== headerData.search) {
        setHeaderData((prev) => ({ ...prev, search }))
      }
    },
    [headerData.search],
  )

  const setContextError = useCallback((error: ListFarmsErrorState) => {
    setErrorState(error)
  }, [])

  const contextValue = useMemo(
    () => ({
      header: {
        data: headerData,
        setters: {
          setSort,
          setSearch,
        },
      },
      error: {
        value: errorState.value,
        refetch: errorState.refetch,
        set: setContextError,
      },
      setSelectedFarmId: setSelectedFarmId,
      selectedFarmId: selectedFarmId,
    }),
    [headerData, setSort, setSearch, errorState, setContextError, selectedFarmId, setSelectedFarmId],
  )

  return <ListFarmsContext.Provider value={contextValue}>{children}</ListFarmsContext.Provider>
}
